import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ZahlungsabgleichService } from '../../services/zahlung.service';

export interface ZahlungReportDialogData {
  dateFrom?: string;
  dateTo?: string;
  aktenzeichen?: string;
}

@Component({
  selector: 'app-zahlung-report-dialog',
  templateUrl: './zahlung-report-dialog.component.html',
  styleUrls: ['./zahlung-report-dialog.component.scss'],
})
export class ZahlungReportDialogComponent implements OnInit, OnDestroy {
  @ViewChild('previewFrame') previewFrame!: ElementRef<HTMLIFrameElement>;

  mode: 'zeitraum' | 'aktenzeichen' = 'zeitraum';

  dateFrom = '';
  dateTo = '';
  aktenzeichen = '';

  previewUrl: SafeResourceUrl | null = null;
  private blobUrl: string | null = null;

  loadingPreview = false;
  downloadingPdf = false;
  errorMsg = '';

  constructor(
    public dialogRef: MatDialogRef<ZahlungReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ZahlungReportDialogData,
    private zahlungsabgleichService: ZahlungsabgleichService,
    private sanitizer: DomSanitizer,
    private snackBar: MatSnackBar
  ) {
    this.dateFrom = data?.dateFrom || '';
    this.dateTo = data?.dateTo || '';
    this.aktenzeichen = data?.aktenzeichen || '';
    if (this.aktenzeichen) {
      this.mode = 'aktenzeichen';
    }
  }

  ngOnInit(): void {
    // auto-generate a first preview if we have any criteria
    if (this.aktenzeichen || this.dateFrom || this.dateTo) {
      this.updatePreview();
    }
  }

  ngOnDestroy(): void {
    this.revokeBlob();
  }

  private currentOpts() {
    if (this.mode === 'aktenzeichen') {
      return { aktenzeichen: (this.aktenzeichen || '').trim() };
    }
    return { dateFrom: this.dateFrom, dateTo: this.dateTo };
  }

  private revokeBlob(): void {
    if (this.blobUrl) {
      window.URL.revokeObjectURL(this.blobUrl);
      this.blobUrl = null;
    }
  }

  updatePreview(): void {
    this.errorMsg = '';
    this.loadingPreview = true;
    this.zahlungsabgleichService.getReportHtml(this.currentOpts()).subscribe({
      next: (html: string) => {
        this.revokeBlob();
        const blob = new Blob([html], { type: 'text/html' });
        this.blobUrl = window.URL.createObjectURL(blob);
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
        this.loadingPreview = false;
      },
      error: (err) => {
        console.error('report preview failed', err);
        this.errorMsg = 'Vorschau konnte nicht geladen werden.';
        this.loadingPreview = false;
      },
    });
  }

  printReport(): void {
    const frame = this.previewFrame?.nativeElement;
    if (frame && frame.contentWindow) {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } else {
      this.snackBar.open('Bitte zuerst eine Vorschau erstellen.', 'Schließen', { duration: 3000 });
    }
  }

  downloadPdf(): void {
    this.downloadingPdf = true;
    this.zahlungsabgleichService.getReportPdf(this.currentOpts()).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `zahlungsbericht_${new Date().toISOString().split('T')[0]}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.downloadingPdf = false;
      },
      error: (err) => {
        console.error('report pdf failed', err);
        this.downloadingPdf = false;
        this.snackBar.open('Fehler beim Erstellen des PDF.', 'Schließen', { duration: 3000 });
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
